import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ShoppingBag, ChevronLeft, ChevronRight, Eye, CheckCircle, XCircle, Send, Search } from 'lucide-react';
import { useRequisicaoCompra } from '@/features/compras/hooks/useRequisicaoCompra';
import type { StatusRequisicaoCompra } from '@/features/compras/types';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const PAGE_SIZES = [10, 20, 50] as const;

const statusConfig: Record<StatusRequisicaoCompra, { label: string; className: string }> = {
  Rascunho:   { label: 'Rascunho',    className: 'bg-gray-500/10 text-gray-600'   },
  Pendente:   { label: 'Pendente',    className: 'bg-yellow-500/10 text-yellow-600' },
  EmAprovacao:{ label: 'Em Aprovação',className: 'bg-blue-500/10 text-blue-600'   },
  Aprovada:   { label: 'Aprovada',    className: 'bg-green-500/10 text-green-600'  },
  Rejeitada:  { label: 'Rejeitada',   className: 'bg-red-500/10 text-red-600'     },
  Cancelada:  { label: 'Cancelada',   className: 'bg-red-500/10 text-red-600'     },
  Atendida:   { label: 'Atendida',    className: 'bg-purple-500/10 text-purple-600' },
};

const RequisicaoCompra = () => {
  const navigate = useNavigate();
  const [statusFiltro,  setStatusFiltro]  = useState<StatusRequisicaoCompra | undefined>(undefined);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [busca,         setBusca]         = useState('');
  // Busca, período e ordem vão ao servidor: a lista inteira cresce sem limite com o tempo.
  const buscaEnviada = useDebounce(busca.trim(), 400);
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'valor'>('emissao', true, () => setPagina(1));

  const { requisicoes, total, totalPaginas, porStatus, isLoading, isFetching, enviarRequisicao, isEnviando } =
    useRequisicaoCompra({
      pagina, tamanhoPagina, status: statusFiltro,
      filtros: { busca: buscaEnviada, ...periodo.intervalo, ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente },
    });

  // Contagem do conjunto filtrado (período/busca), calculada no servidor — não só desta página.
  const pendentes  = (porStatus?.Pendente ?? 0) + (porStatus?.EmAprovacao ?? 0);
  const aprovadas  = porStatus?.Aprovada ?? 0;
  const rascunhos  = porStatus?.Rascunho ?? 0;
  const firstItem  = (pagina - 1) * tamanhoPagina + 1;
  const lastItem   = Math.min(pagina * tamanhoPagina, total);

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Requisições de Compra</h1>
          <p className="text-muted-foreground mt-1">Gerencie solicitações de compra</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/compras/requisicoes/nova')}>
          <Plus className="h-4 w-4" />Nova Requisição
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShoppingBag className="h-4 w-4 text-gray-500" />Rascunhos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rascunhos}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Send className="h-4 w-4 text-yellow-500" />Pendentes / Em aprovação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{pendentes}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />Aprovadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{aprovadas}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">No período e filtros selecionados</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros (servidor) */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Número ou justificativa..."
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
            className="pl-9"
          />
        </div>
        <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
        <Select
          value={statusFiltro ?? 'todos'}
          onValueChange={v => { setStatusFiltro(v === 'todos' ? undefined : v as StatusRequisicaoCompra); setPagina(1); }}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue placeholder="Todos os status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            {Object.entries(statusConfig).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Data Emissão</CabecalhoOrdenavel>
                <TableHead>Necessidade</TableHead>
                <TableHead>Justificativa</TableHead>
                <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Valor Total</CabecalhoOrdenavel>
                <TableHead>Status</TableHead>
                <TableHead className="w-24">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                </TableRow>
              ) : requisicoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma requisição neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma requisição encontrada'}
                  </TableCell>
                </TableRow>
              ) : (
                requisicoes.map(r => {
                  const cfg = statusConfig[r.status] ?? { label: String(r.status), className: '' };
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono font-bold">#{r.numero}</TableCell>
                      <TableCell>{new Date(r.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                      <TableCell>
                        {r.dtNecessidade
                          ? new Date(r.dtNecessidade).toLocaleDateString('pt-BR')
                          : '—'}
                      </TableCell>
                      <TableCell className="max-w-48 truncate">{r.justificativa ?? '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {r.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell>
                        <Badge className={cfg.className}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" title="Ver detalhes"
                            onClick={() => navigate(`/compras/requisicoes/${r.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {r.status === 'Rascunho' && (
                            <Button size="sm" variant="ghost" title="Enviar para aprovação"
                              disabled={isEnviando}
                              onClick={() => enviarRequisicao(r.id)}>
                              <Send className="h-4 w-4 text-blue-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Itens por página:</span>
          <Select value={String(tamanhoPagina)} onValueChange={v => { setTamanhoPagina(Number(v)); setPagina(1); }}>
            <SelectTrigger className="w-16 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {total > 0 && <span>{firstItem}–{lastItem} de {total}</span>}
          {isFetching && !isLoading && <span className="text-xs">(atualizando...)</span>}
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="flex items-center px-3 text-sm">{pagina} / {totalPaginas}</span>
          <Button variant="outline" size="sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RequisicaoCompra;
